export type UserRole =
  | 'super_admin'
  | 'tenant_admin'
  | 'tenant_member'
  | 'viewer';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthMembership {
  tenantId: string;
  tenantName: string;
  roleName: string;
  roleId: string;
}

export interface AuthAgentGrant {
  preset: string;
  permissions: string[];
}

export interface AuthActiveContext {
  type: 'platform' | 'tenant';
  tenantId: string | null;
}

export interface AuthSessionFlags {
  mustEnrollMfa: boolean;
  mustChangePassword: boolean;
}

/**
 * Enriched session from GET /auth/me, login, refresh, and POST /auth/active-tenant.
 * Matches BE `AuthSession` (P0.4).
 */
export interface AuthUser {
  id: string;
  email: string;
  fullName?: string | null;
  /** Legacy enum — prefer permissions / isSuperAdmin. */
  role: UserRole;
  tenantId: string | null;
  isActive: boolean;
  isPlatformUser: boolean;
  isSuperAdmin: boolean;
  platformRoles: string[];
  memberships: AuthMembership[];
  activeContext: AuthActiveContext;
  permissions: string[];
  agentGrants: Record<string, AuthAgentGrant>;
  agentsUnrestricted: boolean;
  flags: AuthSessionFlags;
}

/** Backend /auth/login and /auth/refresh response (refresh token is a cookie). */
export interface LoginResponse {
  accessToken: string;
  expiresInSeconds: number;
  user: AuthUser;
}

/** Password OK but MFA challenge required (no session yet). */
export interface MfaRequiredResponse {
  status: 'mfa_required';
  mfaToken: string;
  expiresInSeconds: number;
}

export type LoginResult = LoginResponse | MfaRequiredResponse;

export function isMfaRequired(
  result: LoginResult,
): result is MfaRequiredResponse {
  return (result as MfaRequiredResponse).status === 'mfa_required';
}

export interface MfaSetupResponse {
  otpauthUrl: string;
  secret: string;
}

export interface MfaConfirmResponse extends LoginResponse {
  recoveryCodes: string[];
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  tenantId: string | null;
  isAuthenticated: boolean;
}

/** Fill defaults so older partial payloads don't break the shell. */
export function normalizeAuthUser(
  raw: AuthUser | (Partial<AuthUser> & {
    id: string;
    email: string;
    role: UserRole;
    tenantId: string | null;
    isActive: boolean;
  }),
): AuthUser {
  const tenantId = raw.tenantId ?? null;
  const isSuperAdmin =
    raw.isSuperAdmin === true || raw.role === 'super_admin';
  return {
    id: raw.id,
    email: raw.email,
    fullName: raw.fullName ?? null,
    role: raw.role,
    tenantId,
    isActive: raw.isActive,
    isPlatformUser: raw.isPlatformUser ?? isSuperAdmin,
    isSuperAdmin,
    platformRoles: raw.platformRoles ?? (isSuperAdmin ? ['super_admin'] : []),
    memberships: raw.memberships ?? [],
    activeContext: raw.activeContext ?? {
      type: tenantId ? 'tenant' : 'platform',
      tenantId,
    },
    permissions: raw.permissions ?? [],
    agentGrants: raw.agentGrants ?? {},
    agentsUnrestricted: raw.agentsUnrestricted ?? false,
    flags: raw.flags ?? {
      mustEnrollMfa: false,
      mustChangePassword: false,
    },
  };
}
